import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Trophy, 
  Calendar,
  MapPin,
  Users,
  ChevronLeft,
  Loader2,
  Award,
  Swords
} from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/utils';

export default function TournamentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [myTeams, setMyTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  const fetchData = async () => {
    try {
      const [tournamentRes, teamsRes] = await Promise.all([
        api.get(`/tournaments/${id}`),
        api.get('/teams/my'),
      ]);
      setTournament(tournamentRes.data);
      setMyTeams(teamsRes.data?.filter(t => t.captain_id === user?.id) || []);
    } catch (error) {
      toast.error('Turnier nicht gefunden');
      navigate('/tournaments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const isOrganizer = tournament?.organizer_id === user?.id;
  const isRegistered = tournament?.registered_teams?.some(
    t => myTeams.some(mt => mt.id === t.team_id)
  );

  const handleRegister = async () => {
    if (!selectedTeam) {
      toast.error('Bitte wähle ein Team');
      return;
    }

    setRegistering(true);
    try {
      await api.post(`/tournaments/${id}/register?team_id=${selectedTeam}`);
      toast.success('Team erfolgreich angemeldet!');
      await fetchData();
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-64 mb-8" />
          <Skeleton className="h-48 rounded-2xl mb-4" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  if (!tournament) return null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tournament-details">
        {/* Header */}
        <div className="flex items-start gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <Badge className={
              tournament.status === 'registration' ? 'bg-tunis-teal text-white' :
              tournament.status === 'in_progress' ? 'bg-amber-500 text-white' :
              'bg-slate-500 text-white'
            }>
              {tournament.status === 'registration' ? 'Anmeldung offen' :
               tournament.status === 'in_progress' ? 'Läuft' : 'Beendet'}
            </Badge>
            <h1 className="font-display font-bold text-2xl sm:text-3xl text-tunis-blue mt-2">
              {tournament.name}
            </h1>
            <p className="text-muted-foreground mt-1">{tournament.description}</p>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Calendar className="w-6 h-6 text-tunis-blue mx-auto mb-2" />
              <div className="text-sm text-muted-foreground">Datum</div>
              <div className="font-medium">{formatDate(tournament.start_date)}</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <MapPin className="w-6 h-6 text-tunis-blue mx-auto mb-2" />
              <div className="text-sm text-muted-foreground">Ort</div>
              <div className="font-medium truncate">{tournament.field_name}</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-tunis-blue mx-auto mb-2" />
              <div className="text-sm text-muted-foreground">Teams</div>
              <div className="font-medium">{tournament.team_count}/{tournament.max_teams}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-400 to-yellow-500 text-white border-0">
            <CardContent className="p-4 text-center">
              <Trophy className="w-6 h-6 mx-auto mb-2" />
              <div className="text-sm text-white/80">Preisgeld</div>
              <div className="font-bold text-lg">{formatCurrency(tournament.prize_pool)}</div>
            </CardContent>
          </Card>
        </div>

        {/* Registration */}
        {tournament.status === 'registration' && !isRegistered && myTeams.length > 0 && (
          <Card className="border-tunis-teal border-2 mb-8" data-testid="registration-card">
            <CardContent className="p-6">
              <h3 className="font-bold text-lg text-tunis-blue mb-4">Team anmelden</h3>
              <div className="flex gap-4">
                <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Team auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {myTeams.map(team => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name} ({team.member_count} Mitglieder)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  className="btn-primary"
                  onClick={handleRegister}
                  disabled={registering || !selectedTeam}
                >
                  {registering ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <>Anmelden ({formatCurrency(tournament.entry_fee)})</>
                  )}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Team muss mindestens {tournament.team_size} Mitglieder haben
              </p>
            </CardContent>
          </Card>
        )}

        {isRegistered && (
          <Card className="bg-tunis-teal/10 border-tunis-teal mb-8">
            <CardContent className="p-4 flex items-center gap-3">
              <Award className="w-6 h-6 text-tunis-teal" />
              <span className="font-medium text-tunis-teal">Dein Team ist angemeldet!</span>
            </CardContent>
          </Card>
        )}

        {/* Winner */}
        {tournament.status === 'completed' && tournament.winner_team_name && (
          <Card className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white border-0 mb-8">
            <CardContent className="p-6 text-center">
              <Trophy className="w-12 h-12 mx-auto mb-3" />
              <div className="text-white/80">Gewinner</div>
              <div className="font-display font-bold text-2xl">{tournament.winner_team_name}</div>
            </CardContent>
          </Card>
        )}

        {/* Registered Teams */}
        <Card className="border-slate-200 mb-8" data-testid="teams-card">
          <CardHeader>
            <CardTitle className="text-tunis-blue">
              Teilnehmende Teams ({tournament.registered_teams?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {tournament.registered_teams?.length > 0 ? (
              <div className="space-y-3">
                {tournament.registered_teams.map((team, idx) => (
                  <div
                    key={team.team_id}
                    className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl"
                  >
                    <div className="w-8 h-8 bg-tunis-blue/10 rounded-full flex items-center justify-center font-bold text-tunis-blue">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-tunis-blue">{team.team_name}</div>
                      <div className="text-sm text-muted-foreground">ELO: {team.team_elo}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                Noch keine Teams angemeldet
              </p>
            )}
          </CardContent>
        </Card>

        {/* Bracket */}
        {tournament.bracket?.length > 0 && (
          <Card className="border-slate-200" data-testid="bracket-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-tunis-blue">
                <Swords className="w-5 h-5" />
                Turnierbaum
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {tournament.bracket.map((match) => (
                  <div
                    key={match.id}
                    className={`p-4 rounded-xl border-2 ${
                      match.status === 'completed' ? 'border-slate-200 bg-slate-50' : 'border-tunis-teal/50 bg-tunis-teal/5'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline">Runde {match.round}</Badge>
                      <Badge className={match.status === 'completed' ? 'bg-slate-500' : 'bg-amber-500'}>
                        {match.status === 'completed' ? 'Beendet' : 'Ausstehend'}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4 items-center">
                      <div className={`text-center p-2 rounded ${match.winner_id === match.team1_id ? 'bg-green-100 text-green-700' : ''}`}>
                        <div className="font-medium">{match.team1_name}</div>
                        {match.status === 'completed' && (
                          <div className="text-2xl font-bold">{match.score1}</div>
                        )}
                      </div>
                      <div className="text-center text-muted-foreground">VS</div>
                      <div className={`text-center p-2 rounded ${match.winner_id === match.team2_id ? 'bg-green-100 text-green-700' : ''}`}>
                        <div className="font-medium">{match.team2_name || 'TBD'}</div>
                        {match.status === 'completed' && (
                          <div className="text-2xl font-bold">{match.score2}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
